import { isEmpty } from '@ember/utils'
import type { SchoolScope, StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type { NormalizedInterval } from 'date-fns'
import type StudentEventsService from 'teacher-ui-client/services/student-events'
import type { AssessmentScoresAveragesData } from 'teacher-ui-client/components/giraffe/charts/assessment-scores/averages'

export interface AssessmentScoresAveragesResponse {
  assessment_scores_averages_summary_by_grade: SummaryData[]
  assessment_scores_averages_by_grade: GradeData[]
}

interface SummaryData {
  grade_position: number
  data: [
    { category: 'average_attempts'; count: number },
    { category: 'average_score'; count: number },
    {
      category: 'scores'
      values: [
        { range: '0-49'; student_count: number },
        { range: '50-79'; student_count: number },
        { range: '80-100'; student_count: number },
      ]
    },
  ]
}

interface GradeData {
  grade_position: number
  maps: { attempts: number; average_score: number; map: number; student_count: number }[]
}

export async function assessmentScoresAverages(
  this: StudentEventsService,
  product: LegacyProductRe | LegacyProductRex | LegacyProductMs,
  statsScope: SchoolScope | StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<AssessmentScoresAveragesData[]>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('assessment_scores_averages_by_grade', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<AssessmentScoresAveragesResponse>(url, query)
  if (!data) return null
  const {
    assessment_scores_averages_summary_by_grade: summaryData,
    assessment_scores_averages_by_grade: averagesData,
  } = data
  if (summaryData.length === 0) return null

  return groupByGrade(summaryData, averagesData)
}

function groupByGrade(summaryData: SummaryData[], gradeData: GradeData[]): AssessmentScoresAveragesData[] {
  const sortedSummaryData = [...summaryData].sort((a, b) => a.grade_position - b.grade_position)

  return sortedSummaryData
    .map((summary) => {
      const currentGradeData = gradeData.find((value) => value.grade_position === summary.grade_position)
      if (!currentGradeData) return null

      const average_score = summary.data.find((value) => value.category === 'average_score')?.count ?? 0
      const scores = summary.data.find((value) => value.category === 'scores')?.values ?? [
        { range: '0-49', student_count: 0 },
        { range: '50-79', student_count: 0 },
        { range: '80-100', student_count: 0 },
      ]

      return {
        grade_position: summary.grade_position,
        average_score,
        scores,
        maps: currentGradeData.maps,
      }
    })
    .filter((data) => data !== null)
}
